// File: utils/restApi/use-form.tsx
import * as React from 'react';
import { TypeParser } from '../form';
import type {IRestModel, PropertyMap, RestApiFormHooks, ValidationMap, UseFormReturn} from './types';



type NameValue = {
    name  : string;
    value : string;
};

const getNameValue = (e: React.FormEvent<unknown>): NameValue | null => {
    const input: unknown = (e as { target?: unknown }).target;
    if (!input || typeof input !== 'object') return;

    const t     = input as Record<string, unknown>;
    const name  = typeof t.name === 'string'  ? t.name  : undefined;
    const value = typeof t.value === 'string' ? t.value : undefined;
    if( !name ) return;

    return { name, value };
}

export function useForm<T extends IRestModel>(
    api          : RestApiFormHooks<T>,
    id           : string | number | null,
    formMappers ?: PropertyMap<T> | null,
    validators  ?: ValidationMap<T> | null,
    defaultObj  ?: T,
): UseFormReturn<T> {
    if (id === undefined || id === '') { //id must be set or ===null for create
        throw new Error('[useForm] Invalid id: id must be null (for add) or a valid value (for edit).');
    }

    defaultObj                      = defaultObj || ({} as T);
    const result                    = api.useGetById(id);
    const isLoaded                  = id === null || !!result?.payload;
    const initialData               = (id ? result?.payload : null) || defaultObj;
    const isEditMode                = id != null;
    const { submit: create }        = api.useCreate();
    const { submit: update }        = isEditMode ? api.useUpdate(id as string | number) : api.useUpdate((0 as unknown) as string); // TODO: ensure api.useUpdate is never called when id is null
    const submitFn                  = isEditMode ? update : create;

    const [data, setData]           = React.useState<T>(initialData);
    const [errors, setErrors]       = React.useState<Record<keyof T & string, string>>({} as Record<keyof T & string, string>);
    const [isDirty, setDirty]       = React.useState<boolean>(false);
    const [message, setMessage]     = React.useState<string>('Loading...');
    const [isMutating, setMutating] = React.useState(false);

    // Optional callback a page can register to run after a successful save
    const onSaveCompleteRef         = React.useRef<null | ((payload: T) => void)>(null);
    // last synced snapshot & id for reset / clobber rules
    const lastSynced                = React.useRef<T | null>(null);
    const lastLoadedId              = React.useRef<string | number | null>(null);

    // Initial sync + resync on id change or when not dirty
    React.useEffect(() => {
        if (id === null || !result?.payload) {
            setMessage(id === null ? '' : 'Loading...');
            return;
        }

        const incomingId    = result.payload.id;
        const idChanged     = incomingId !== undefined && incomingId !== lastLoadedId.current;

        if (idChanged || !isDirty || lastSynced.current === null) {
            if (lastSynced.current !== result.payload) {
                setData(result.payload);
                setErrors({} as Record<keyof T & string, string>);
                setDirty(false);
                lastSynced.current = result.payload;
                lastLoadedId.current = incomingId ?? null;
            }
        }
    }, [id, result?.payload, isDirty]);

    // Helpers
    const validateField = React.useCallback((key: keyof T & string, value: unknown) => {
        const fn        = validators?.[key];
        if (!fn) return null;
        const result    = fn(value as string);
        return result.isValid ? null : result.message;
    }, [validators]);

    const setField = React.useCallback((key: keyof T & string, value: any) => {
        setData(prev => {
            const current           = prev[key] as unknown;
            let nextValue : unknown = value;

            if (formMappers && formMappers[key]) {
                nextValue = formMappers[key](value);
            }
            else if( typeof current === 'number' ) {
                nextValue = value === '' ? '' : Number(value);
            }

            return {...prev, [key]: nextValue};
        });
        setDirty(true);
    }, [formMappers]);

    const setError = React.useCallback((key: keyof T & string, message: string | null) => {
        setErrors(prev => {
            const next      = { ...prev };
            if (!message) delete next[key];
            else next[key]  = message;

            return next;
        });
    }, []);

    const handleStringChange = React.useCallback((e: React.FormEvent<unknown>) => {
        const { name, value } = getNameValue(e) || {};
        if( name ) setField(name as keyof T & string, value);
    }, [setField]);

    const handleIntChange = React.useCallback((e: React.FormEvent<unknown>) => {
        const { name, value }   = getNameValue(e) || {};
        const result            = TypeParser.getIntOrDefault(value, null);

        if( name ) setField(name as keyof T & string, result);
    }, [setField]);

    const handleBoolChange = React.useCallback((e: React.FormEvent<unknown>) => {
        const { name, value }   = getNameValue(e) || {};
        const result            = TypeParser.getBoolOrDefault(value, null);
        
        if( name ) setField(name as keyof T & string, result);
    }, [setField]);

    const getStringToggleGroupHandler = React.useCallback(( fieldName : string ) => {
        const handler = (...args: any[]) => {
            const first             = args[0]; // could be values array or single value; older typings also pass (values, event)
            const values : any[]    = Array.isArray(first) ? first : [first];
            const arr               = values.map(v => String(v));

            setField(fieldName as keyof T & string, arr);
        };
        return handler as any;
    }, [setField]);

    const getIntToggleGroupHandler = React.useCallback(( fieldName : string ) => {
        const handler = (...args: any[]) => {
            const first             = args[0];
            const values : any[]    = Array.isArray(first) ? first : [first];
            const arr               = values.map(v => Number(v));

            setField(fieldName as keyof T & string, arr);
        };
        return handler as any;
    }, [setField]);

    // Factory that returns an event handler for a specific date format.
    // NOTE: Previously this called React.useCallback INSIDE the returned factory, which is a Rules-of-Hooks violation.
    // We now have a single top-level useCallback whose result is a stable factory function.
    const getDateHandler = React.useCallback((format: string) => {
        return (e: React.FormEvent<unknown>) => {
            const { name, value } = getNameValue(e) || {};
            const parsedDate      = TypeParser.getDateByFormatOrDefault(value, format, null);
            const formatted       = parsedDate ? TypeParser.formatDate(parsedDate, format) : undefined;
            if (name) setField(name as keyof T & string, formatted);
        };
    }, [setField]);

    const handleBlur = React.useCallback((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = getNameValue(e) || {};

        if( name ) {
            e.target?.classList?.add('touched');

            const err = validateField(name as keyof T & string, value);

            setError(name as keyof T & string, err);
        }
    }, [setError, validateField]);

    const runAllValidators = React.useCallback(() => {
        const next: Record<string, string> = {};
        if (!validators) return next;

        for (const k in validators) {
            const key = k as keyof T & string;
            const fn = validators[key];

            if (!fn) continue;

            const result = fn(String(data[key]));

            if (!result.isValid) next[key] = result.message;
        }
        return next;
    }, [data, validators]);

    const handleSubmit = React.useCallback(async (e: React.FormEvent): Promise<boolean> => {
        e.preventDefault();
        if (isMutating) return false; // prevent double submit

        setMutating(true);

        try {
            const clientErrors = runAllValidators();

            if (Object.keys(clientErrors).length > 0) {
                setErrors(clientErrors as Record<keyof T & string, string>);
                return false;
            }

            const payload: T = lastLoadedId.current
                ? ({ ...data, id: lastLoadedId.current } as T)
                : data;

            const res = await submitFn(payload);

            if (res?.isSuccess) {
                setErrors({} as Record<keyof T & string, string>);
                setDirty(false);
                if (res.payload) {
                    setData(res.payload);
                    lastSynced.current = res.payload;
                    // Notify listeners the save completed successfully
                    try { onSaveCompleteRef.current?.(res.payload as T); } catch { /* ignore */ }
                }
                
                return true;
            }
            else if (res?.errors) {
                setErrors(res.errors as Record<keyof T & string, string>);

                if (!isLoaded) {
                    setMessage('Please resolve the errors above.');
                }

                return false;
            }

            return false;
        }
        finally {
            setMutating(false);
        }
    }, [data, isMutating, isLoaded, runAllValidators, submitFn]);

    const reset = React.useCallback(() => {
        const baseline = lastSynced.current ?? (result?.payload ?? ({} as T));
        setData(baseline);
        setErrors({} as Record<keyof T & string, string>);
        setDirty(false);
    }, [result]);

    // Programmatic replace that can optionally also set a new baseline and dirty flag
    const replaceData = React.useCallback((next: T, options?: { baseline?: boolean; dirty?: boolean }) => {
        setData(next);
        const markDirty = options?.dirty === true;
        setDirty(markDirty);

        if (options?.baseline !== false) {
            lastSynced.current   = next;
            // If the incoming object has an id, adopt it as the current loaded id for future updates
            const nextId = (next as any)?.id;
            if (nextId !== undefined) {
                lastLoadedId.current = nextId as string | number;
            }
            setErrors({} as Record<keyof T & string, string>);
        }
    }, []);

    return {
        data,
        errors,
        isDirty,
        isMutating,
        isLoaded,
        message,

        handleStringChange,
        handleIntChange,
        handleBoolChange,
        getDateHandler,
        getStringToggleGroupHandler,
        getIntToggleGroupHandler,
        handleBlur,
        handleSubmit,
        reset,
        replaceData,

        setField,
        setError,
        setOnSaveComplete: (cb) => { onSaveCompleteRef.current = cb; },
    };
}
